import { useState, useEffect } from 'react';
import {
  Container,
  Tabs,
  Text,
  Accordion,
  Card,
  TextInput,
  Textarea,
  Button,
  Group,
  Select,
  Paper,
  Stack,
  Box,
  Loader,
  Center
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle, IconTicket, IconPhone } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import supportService, { ticketCategories, TicketFormData } from '../features/support/supportService';
import PageTitle from '../components/common/PageTitle';

// Support Ticket Form
const SupportTicketForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const form = useForm<TicketFormData>({
    initialValues: {
      subject: '',
      category: '',
      description: '',
      priority: 'medium' as 'medium' | 'low' | 'high'
    },
    validate: {
      subject: (value) => (value.length < 5 ? 'Subject must be at least 5 characters' : null),
      category: (value) => (!value ? 'Please select a category' : null),
      description: (value) => (value.length < 20 ? 'Description must be at least 20 characters' : null),
    }
  });

  const handleSubmit = async (values: TicketFormData) => {
    setSubmitting(true);

    try {
      // Create ticket using the support service
      await supportService.createTicket(values);

      // Show success notification
      notifications.show({
        title: 'Ticket Submitted',
        message: 'Your support ticket has been submitted successfully. We\'ll respond soon.',
        color: 'green'
      });

      // Reset form
      form.reset();

      // Navigate to tickets page
      navigate('/tickets');
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to submit your support ticket. Please try again.',
        color: 'red'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper p="md" withBorder>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            label="Subject"
            placeholder="Brief description of your issue"
            required
            {...form.getInputProps('subject')}
          />

          <Select
            label="Category"
            placeholder="Select the category that best matches your issue"
            data={ticketCategories}
            required
            {...form.getInputProps('category')}
          />

          <Select
            label="Priority"
            placeholder="Select priority level"
            data={[
              { value: 'low', label: 'Low - General question' },
              { value: 'medium', label: 'Medium - Issue affecting usage' },
              { value: 'high', label: 'High - Urgent problem' }
            ]}
            {...form.getInputProps('priority')}
          />

          <Textarea
            label="Description"
            placeholder="Please provide as much detail as possible about your issue"
            minRows={4}
            required
            {...form.getInputProps('description')}
          />

          <Group justify="flex-end">
            <Button type="submit" loading={submitting}>Submit Ticket</Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  );
};

// FAQs Component
const FAQs = () => {
  const [faqs, setFaqs] = useState<{ question: string; answer: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const data = await supportService.getFAQs();
        setFaqs(data);
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: 'Failed to load FAQ data',
          color: 'red'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  if (loading) {
    return (
      <Center p="xl">
        <Loader size="md" />
      </Center>
    );
  }

  return (
    <Accordion>
      {faqs.map((faq, index) => (
        <Accordion.Item key={index} value={`faq-${index}`}>
          <Accordion.Control>{faq.question}</Accordion.Control>
          <Accordion.Panel>
            <Text>{faq.answer}</Text>
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
};

// Contact Information Component
const ContactInfo = () => {
  return (
    <Stack>

      <Paper p="md" withBorder>
        <Group>
          <IconPhone size="2rem" />
          <Box>
            <Text fw={500}>Phone Support</Text>
            <Text>+94 77 784 6800</Text>
            <Text size="sm" c="dimmed">For urgent matters</Text>
          </Box>
        </Group>
      </Paper>

      <Paper p="md" withBorder>
        <Group>
          <Box>
            <Text fw={500}>WhatsApp Chat</Text>
            <Text>Chat with us on WhatsApp during business hours</Text>
            <Button
              variant="light"
              size="xs"
              mt="xs"
              component="a"
              href="https://wa.me/94777846800"
              target="_blank"
              rel="noopener noreferrer"
            >
              Chat on WhatsApp
            </Button>
          </Box>
        </Group>
      </Paper>

      <Paper p="md" withBorder>
        <Group>
          <IconTicket size="2rem" />
          <Box>
            <Text fw={500}>Email Support</Text>
            <Text>sudesh@otlcolombo.com</Text>
            <Text size="sm" c="dimmed">Response within 24 hours</Text>
          </Box>
        </Group>
      </Paper>
    </Stack>
  );
};

// Knowledge Base Component
const KnowledgeBase = () => {
  const [articles, setArticles] = useState<{ title: string; content: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchKnowledgeBase = async () => {
      try {
        const data = await supportService.getKnowledgeBaseArticles();
        setArticles(data);
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: 'Failed to load knowledge base articles',
          color: 'red'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledgeBase();
  }, []);

  if (loading) {
    return (
      <Center p="xl">
        <Loader size="md" />
      </Center>
    );
  }

  return (
    <Stack>
      {articles.map((article, index) => (
        <Card key={index} withBorder>
          <Text fw={700} mb="xs">{article.title}</Text>
          <Text size="sm">{article.content}</Text>
        </Card>
      ))}
    </Stack>
  );
};

// Main Help Component
const Help = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<string | null>(tabParam || 'contact');

  return (

    <Container size="lg">
      <PageTitle title="Help & Support">
      </PageTitle>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="contact" leftSection={<IconPhone size="0.8rem" />}>
            Contact Us
          </Tabs.Tab>
          <Tabs.Tab value="faq" leftSection={<IconInfoCircle size="0.8rem" />}>
            FAQs
          </Tabs.Tab>
          {/* <Tabs.Tab value="ticket" leftSection={<IconTicket size="0.8rem" />}>
            Support Ticket
          </Tabs.Tab> */}
        </Tabs.List>

        <Tabs.Panel value="faq">
          <Text mb="md">Frequently Asked Questions about OTLExpress services</Text>
          <FAQs />
        </Tabs.Panel>

        <Tabs.Panel value="kb">
          <Text mb="md">Browse our knowledge base for detailed information and guides</Text>
          <KnowledgeBase />
        </Tabs.Panel>

        <Tabs.Panel value="ticket">
          <Text mb="md">Submit a support ticket and we'll get back to you as soon as possible</Text>
          <SupportTicketForm />
        </Tabs.Panel>

        <Tabs.Panel value="contact">
          <Text mb="md">Contact our support team through multiple channels</Text>
          <ContactInfo />
        </Tabs.Panel>
      </Tabs>
    </Container>

  );
};

export default Help;
